"use client";

import { useComboInstrumentsController } from "#/hooks/useComboInstruments.controller";
import { useStrikes } from "#/hooks/useStrikes";
import { useMarketIndexPrice } from "#/store/app/indexPrices/marketIndexPrice";
import type { OptionType } from "#/types/options";
import { nextMaturities } from "#/utils/maturities";

export const useCalendar = (optionType: OptionType) => {
  const {
    comboPerpInstruments,
    comboOptionInstruments,
    handleAddLegs,
    buildInstrument,
  } = useComboInstrumentsController();
  const [maturityA, , maturityB] = nextMaturities();
  const { marketIndexPrice } = useMarketIndexPrice();
  const { strikes: strikesA } = useStrikes(maturityA);
  const { strikes: strikesB } = useStrikes(maturityB);

  // Apply calendar spread: 1 buy (far expiry), 1 sell (near expiry) for given optionType
  // Note: Users can flip directions to create reverse calendar spread
  const applyCalendar = () => {
    const commonStrikes = strikesA.filter((strike) =>
      strikesB.includes(strike),
    );

    const nearestStrike =
      commonStrikes.find((s) => s >= marketIndexPrice) || commonStrikes.at(-1);

    if (nearestStrike === undefined) return;

    handleAddLegs([
      buildInstrument({
        type: "options",
        side: "buy",
        optionType,
        maturity: maturityA, // nearer expiry
        strike: nearestStrike,
      }),
      buildInstrument({
        type: "options",
        side: "sell",
        optionType,
        maturity: maturityB, // further expiry
        strike: nearestStrike,
      }),
    ]);
  };

  const isActivePatternCalendar = () => {
    if (comboPerpInstruments.length > 0 || comboOptionInstruments.length !== 2)
      return false;

    const [a, b] = comboOptionInstruments;
    if (
      a.optionType !== optionType ||
      b.optionType !== optionType ||
      a.strike !== b.strike ||
      a.underlying !== b.underlying ||
      a.maturity === b.maturity
    )
      return false;

    // Supports both calendar spreads and reverse calendar spreads (opposite sides)
    return a.side !== b.side;
  };

  return { applyCalendar, isActivePatternCalendar };
};
